import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ManageAccountsIcon     from '@mui/icons-material/ManageAccounts';
import CalendarTodayIcon      from '@mui/icons-material/CalendarToday';
import EventAvailableIcon     from '@mui/icons-material/EventAvailable';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import { getApp }             from 'firebase/app';
import {
  getStripePayments,
  createCheckoutSession,
} from '@invertase/firestore-stripe-payments';
import { httpsCallable }  from 'firebase/functions';
import { functions }      from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import {
  PLAN_LIMITS,
  PRICE_IDS,
  STRIPE_COLLECTIONS,
  STRIPE_REDIRECT,
  PLANS,
  getT,
  calcFinalPrice,   // ← nuevo import
} from './stripePlansConfig';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const pulse = keyframes`
  0%, 100% { transform: scale(1);    opacity: 1;    }
  50%       { transform: scale(1.06); opacity: 0.85; }
`;
// Animación para el badge de descuento
const popIn = keyframes`
  0%   { transform: scale(0.7); opacity: 0; }
  70%  { transform: scale(1.08); }
  100% { transform: scale(1);   opacity: 1; }
`;

// ─── Page wrapper ─────────────────────────────────────────────────────────────
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  background: 'linear-gradient(160deg, #f0f4ff 0%, #fafafa 55%, #f5f0ff 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'radial-gradient(circle at 10% 15%, rgba(99,102,241,0.07) 0%, transparent 45%), ' +
      'radial-gradient(circle at 90% 85%, rgba(168,85,247,0.06) 0%, transparent 45%)',
    pointerEvents: 'none',
  },
});

// ─── Plan card ────────────────────────────────────────────────────────────────
const PlanCard = styled(Card)(({ iscurrent }) => ({
  position: 'relative',
  borderRadius: 20,
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  animation: `${fadeUp} 0.45s ease both`,
  ...(iscurrent
    ? {
        background: 'linear-gradient(145deg, #6366f1 0%, #7c3aed 100%)',
        border: '2px solid #6366f1',
        boxShadow: '0 16px 48px rgba(99,102,241,0.32)',
        transform: 'translateY(-6px)',
        '&:hover': { transform: 'translateY(-10px)', boxShadow: '0 28px 64px rgba(99,102,241,0.4)' },
      }
    : {
        background: '#ffffff',
        border: '1.5px solid #e5e7eb',
        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 32px rgba(0,0,0,0.1)', borderColor: '#c7d2fe' },
      }),
}));

// ─── CTA button ───────────────────────────────────────────────────────────────
const ActionButton = styled(Button)(({ iscurrent }) => ({
  borderRadius: 12,
  padding: '11px 24px',
  fontFamily: '"DM Sans", sans-serif',
  fontWeight: 700,
  letterSpacing: '0.01em',
  textTransform: 'none',
  fontSize: '0.9rem',
  transition: 'all 0.2s ease',
  width: '100%',
  ...(iscurrent
    ? {
        background: 'rgba(255,255,255,0.2)',
        color: '#fff',
        border: '1.5px solid rgba(255,255,255,0.4)',
        '&:hover': { background: 'rgba(255,255,255,0.28)' },
        '&:disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)', border: '1.5px solid rgba(255,255,255,0.15)' },
      }
    : {
        background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
        backgroundSize: '200% 200%',
        color: '#fff',
        border: 'none',
        boxShadow: '0 4px 14px rgba(99,102,241,0.28)',
        '&:hover': { boxShadow: '0 8px 24px rgba(99,102,241,0.45)', transform: 'translateY(-1px)' },
        '&:disabled': { background: '#e5e7eb', color: '#9ca3af', boxShadow: 'none' },
      }),
}));

// ─── Feature row ──────────────────────────────────────────────────────────────
const FeatureItem = ({ text, light }) => (
  <ListItem disableGutters disablePadding sx={{ mb: 0.7 }}>
    <ListItemIcon sx={{ minWidth: 26 }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 15, color: light ? 'rgba(255,255,255,0.7)' : '#6366f1' }} />
    </ListItemIcon>
    <ListItemText
      primary={text}
      primaryTypographyProps={{
        fontSize: '0.82rem',
        color: light ? 'rgba(255,255,255,0.8)' : '#6b7280',
        fontFamily: '"DM Sans", sans-serif',
      }}
    />
  </ListItem>
);

// ─── Discount Badge ───────────────────────────────────────────────────────────
// Muestra "X% OFF" posicionado en la esquina superior-derecha de la card.
const DiscountBadge = ({ pct, isCurrent, label }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 14,
      right: 14,
      px: 1.1,
      py: 0.4,
      borderRadius: 2,
      background: isCurrent
        ? 'rgba(255,255,255,0.22)'
        : 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
      border: isCurrent ? '1px solid rgba(255,255,255,0.35)' : 'none',
      boxShadow: isCurrent ? 'none' : '0 3px 10px rgba(245,158,11,0.4)',
      animation: `${popIn} 0.4s cubic-bezier(0.34,1.56,0.64,1) both`,
      animationDelay: '0.25s',
      zIndex: 2,
    }}
  >
    <Typography
      sx={{
        fontFamily: '"DM Sans", sans-serif',
        fontWeight: 800,
        fontSize: '0.67rem',
        letterSpacing: '0.07em',
        color: isCurrent ? '#fff' : '#fff',
      }}
    >
      {label}
    </Typography>
  </Box>
);

// ─── Timestamp helpers ────────────────────────────────────────────────────────
const tsToDate = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  if (typeof ts?.seconds === 'number') return new Date(ts.seconds * 1000);
  if (typeof ts === 'number') return new Date(ts * 1000);
  if (ts instanceof Date) return ts;
  return null;
};

const fmtDate = (ts, lang) => {
  const d = tsToDate(ts);
  if (!d) return '—';
  const locale = lang === 'es' ? 'es-MX' : 'en-US';
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });
};

// ─── Subscription details panel ───────────────────────────────────────────────
const SubscriptionInfoInline = ({ subscription, t, lang }) => {
  if (!subscription) return null;

  const statusLabel = t.status[subscription.status] ?? subscription.status ?? '—';
  const STATUS_STYLES = {
    active:     { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', dot: '#22c55e' },
    trialing:   { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', dot: '#60a5fa' },
    canceled:   { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', dot: '#f87171' },
    past_due:   { color: '#d97706', bg: '#fffbeb', border: '#fde68a', dot: '#fbbf24' },
    unpaid:     { color: '#d97706', bg: '#fffbeb', border: '#fde68a', dot: '#fbbf24' },
    incomplete: { color: '#ca8a04', bg: '#fefce8', border: '#fef08a', dot: '#facc15' },
  };
  const cfg = STATUS_STYLES[subscription.status] ?? { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', dot: '#9ca3af' };

  const isCanceled = subscription.status === 'canceled';
  const startDate  = fmtDate(subscription.current_period_start ?? subscription.created, lang);
  const endDate    = fmtDate(subscription.current_period_end, lang);
  const canceledAt = subscription.canceled_at ? fmtDate(subscription.canceled_at, lang) : null;
  const endTs      = tsToDate(subscription.current_period_end);
  const daysLeft   = endTs ? Math.ceil((endTs - Date.now()) / 86_400_000) : null;

  const Row = ({ icon, label, value, valueColor }) => (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box sx={{ width: 36, height: 36, borderRadius: 2, background: '#f5f3ff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.cloneElement(icon, { sx: { fontSize: 17, color: '#6366f1' } })}
      </Box>
      <Box>
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: '#9ca3af', fontSize: '0.7rem', lineHeight: 1, mb: 0.3 }}>{label}</Typography>
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: valueColor ?? '#111827', fontSize: '0.87rem', fontWeight: 600 }}>{value}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      p={3.5}
      sx={{
        background: '#fff',
        border: '1.5px solid #e5e7eb',
        borderRadius: 4,
        boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
        animation: `${fadeUp} 0.5s ease both`,
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, color: '#111827', fontSize: '0.97rem' }}>
          {t.detailsTitle}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={0.8}
          px={1.6}
          py={0.55}
          sx={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 50 }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot }} />
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.72rem', color: cfg.color, letterSpacing: '0.05em' }}>
            {statusLabel}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#f3f4f6', mb: 2.5 }} />

      <Box display="flex" flexDirection="column" gap={2}>
        <Row icon={<CalendarTodayIcon />}  label={t.currentPeriodStart} value={startDate} />
        <Row
          icon={<EventAvailableIcon />}
          label={isCanceled ? t.accessUntil : t.nextRenewal}
          value={endDate}
          valueColor={isCanceled ? '#dc2626' : '#111827'}
        />
        {daysLeft !== null && (
          <Row
            icon={<AccessTimeIcon />}
            label={t.daysLeft}
            value={
              daysLeft > 0
                ? t.days(daysLeft)
                : daysLeft === 0
                ? t.expiresToday
                : t.expiredDaysAgo(Math.abs(daysLeft))
            }
            valueColor={
              daysLeft <= 0 ? '#dc2626'
              : daysLeft <= 5 ? '#d97706'
              : '#111827'
            }
          />
        )}
      </Box>

      {canceledAt && (
        <Box mt={2.5} p={1.5} sx={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2 }}>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: '#dc2626', fontSize: '0.78rem' }}>
            {t.canceledWarning(canceledAt)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Stripe overlay ───────────────────────────────────────────────────────────
const StripeOverlay = ({ type, t }) => {
  const isPago = type === 'pago';
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(10px)',
        animation: `${fadeIn} 0.25s ease both`,
      }}
    >
      <Box
        sx={{
          width: 68,
          height: 68,
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #6366f1, #7c3aed)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 36px rgba(99,102,241,0.38)',
          animation: `${pulse} 1.8s ease infinite`,
        }}
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          <path d="M17 11H7V7a5 5 0 0 1 10 0v4Z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
          <rect x="4" y="11" width="16" height="11" rx="3" fill="white" fillOpacity="0.2" stroke="#fff" strokeWidth="2" />
          <circle cx="12" cy="17" r="1.5" fill="#fff" />
        </svg>
      </Box>
      <Box textAlign="center">
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800, fontSize: '1.18rem', color: '#111827', mb: 0.75 }}>
          {isPago ? t.redirectingPayment : t.redirectingPortal}
        </Typography>
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: '#6b7280', fontSize: '0.87rem' }}>
          {isPago ? t.preparingPayment : t.preparingPortal}
        </Typography>
      </Box>
      <CircularProgress size={32} thickness={3} sx={{ color: '#6366f1' }} />
      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: '#d1d5db', fontSize: '0.72rem', mt: 0.5 }}>
        {t.secureLabel}
      </Typography>
    </Box>
  );
};

// ─── Portal card ──────────────────────────────────────────────────────────────
const PortalCard = ({ t, stripeOverlay, onOpenPortal }) => (
  <Box
    p={3.5}
    sx={{
      background: '#fff',
      border: '1.5px solid #e5e7eb',
      borderRadius: 4,
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      height: '100%',
      boxSizing: 'border-box',
    }}
  >
    <ManageAccountsIcon sx={{ color: '#6366f1', fontSize: 30, mb: 0.5 }} />
    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, color: '#111827', fontSize: '0.97rem' }}>
      {t.manageTitle}
    </Typography>
    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: '#6b7280', fontSize: '0.81rem', lineHeight: 1.6, maxWidth: 300 }}>
      {t.manageBody}
    </Typography>
    <Button
      onClick={onOpenPortal}
      disabled={!!stripeOverlay}
      sx={{
        mt: 1.5,
        borderRadius: 10,
        px: 3,
        py: 1.1,
        fontFamily: '"DM Sans", sans-serif',
        fontWeight: 700,
        fontSize: '0.87rem',
        textTransform: 'none',
        border: '1.5px solid #c7d2fe',
        color: '#4f46e5',
        background: '#f5f3ff',
        '&:hover': { background: '#ede9fe', borderColor: '#6366f1' },
        '&:disabled': { background: '#f3f4f6', color: '#9ca3af', borderColor: '#e5e7eb' },
      }}
    >
      {t.openPortal}
    </Button>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SuscripcionStripe() {
  const {
    currentUser,
    planKey,
    historicPlanKey,
    planName,
    isActive,
    subscription,
    loading: subLoading,
    language,
  } = useAuth();

  const t = getT(language);

  const currentPlan    = planKey ?? 'free';
  const hasHadPaidPlan = historicPlanKey && historicPlanKey !== 'free';
  const subStatus      = subscription?.status ?? null;
  const pageLoading    = subLoading;

  const [loadingPlan,   setLoadingPlan]   = useState(null);
  const [stripeOverlay, setStripeOverlay] = useState(null);
  const [errorMsg,      setErrorMsg]      = useState('');
  const [snackOpen,     setSnackOpen]     = useState(false);
  const [confirmPlan,   setConfirmPlan]   = useState(null);

  const urlParams   = new URLSearchParams(window.location.search);
  const fromSuccess = urlParams.get('success')  === 'true';
  const fromCancel  = urlParams.get('canceled') === 'true';

  const payments = React.useMemo(() => {
    try {
      return getStripePayments(getApp(), {
        productsCollection: STRIPE_COLLECTIONS.products,
        customersCollection: STRIPE_COLLECTIONS.customers,
      });
    } catch (e) { console.error('Stripe init error', e); return null; }
  }, []);

  // ── Subscribe ─────────────────────────────────────────────────────────────
  const handleSubscribe = (pk) => {
    if (pk === 'free' || pk === currentPlan) return;
    setConfirmPlan(pk);
  };

  const confirmSubscribe = async () => {
    const pk = confirmPlan;
    setConfirmPlan(null);
    setLoadingPlan(pk);
    setStripeOverlay('pago');
    try {
      const priceId = PRICE_IDS[pk];
      if (!priceId) throw new Error('Price ID not configured for ' + pk);
      const session = await createCheckoutSession(payments, {
        mode: 'subscription',
        price: priceId,
        success_url: STRIPE_REDIRECT.success,
        cancel_url:  STRIPE_REDIRECT.cancel,
        allow_promotion_codes: true,
      });
      const url = session.url?.startsWith('http') ? session.url : `https://${session.url}`;
      window.location.href = url;
    } catch (err) {
      console.error(err);
      setErrorMsg(language === 'es'
        ? 'Error al iniciar el pago. Por favor intenta de nuevo.'
        : 'Error starting payment. Please try again.');
      setSnackOpen(true);
      setLoadingPlan(null);
      setStripeOverlay(null);
    }
  };

  // ── Billing portal ────────────────────────────────────────────────────────
  const handlePortal = async () => {
    setStripeOverlay('portal');
    try {
      const createPortalLink = httpsCallable(
        functions,
        'ext-firestore-stripe-payments-createPortalLink'
      );
      const { data } = await createPortalLink({ returnUrl: window.location.href });
      const url = data.url?.startsWith('http') ? data.url : `https://${data.url}`;
      window.location.href = url;
    } catch (err) {
      console.error(err);
      setErrorMsg(language === 'es'
        ? 'No se pudo abrir el portal de facturación.'
        : 'Could not open the billing portal.');
      setSnackOpen(true);
    } finally {
      setStripeOverlay(null);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <PageWrapper display="flex" alignItems="center" justifyContent="center">
        <CircularProgress sx={{ color: '#6366f1' }} />
      </PageWrapper>
    );
  }

  // ── Condiciones de visibilidad ────────────────────────────────────────────
  const showBottomSection = !!subscription || currentPlan !== 'free';
  const showDetails       = !!subscription;
  const showPortal        = currentPlan !== 'free' || hasHadPaidPlan;
  const gridCols          = showDetails && showPortal
    ? { xs: '1fr', md: '1fr 1fr' }
    : '1fr';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageWrapper>
      <Container maxWidth="xl" sx={{ position: 'relative', pt: { xs: 6, md: 10 }, pb: 8 }}>

        {/* Return banners */}
        {fromSuccess && (
          <Box mb={3} p={2.5} sx={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ fontSize: '1.4rem' }}>🎉</Box>
            <Box>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, color: '#15803d', fontSize: '0.95rem' }}>{t.successTitle}</Typography>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: '#16a34a', fontSize: '0.8rem' }}>{t.successBody}</Typography>
            </Box>
          </Box>
        )}
        {fromCancel && (
          <Box mb={3} p={2.5} sx={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: '#92400e', fontSize: '0.88rem' }}>{t.cancelBanner}</Typography>
          </Box>
        )}

        {/* Header */}
        <Box textAlign="center" mb={{ xs: 5, md: 7 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '2.9rem' },
              fontWeight: 800,
              fontFamily: '"DM Sans", sans-serif',
              color: '#111827',
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            {t.pageTitle}
          </Typography>
          <Typography sx={{ color: '#6b7280', fontFamily: '"DM Sans", sans-serif', fontSize: '0.97rem', maxWidth: 420, mx: 'auto', lineHeight: 1.7 }}>
            {t.pageSubtitle}
          </Typography>

          {(currentPlan !== 'free' || subscription) && (
            <Box
              mt={3}
              display="inline-flex"
              alignItems="center"
              gap={1}
              px={2.5}
              py={0.9}
              sx={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 50 }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e' }} />
              <Typography sx={{ color: '#15803d', fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600 }}>
                {t.activePlan}:{' '}
                <span style={{ textTransform: 'capitalize' }}>
                  {currentPlan !== 'free' ? currentPlan : historicPlanKey}
                </span>
                {subStatus && (
                  <span style={{ opacity: 0.7 }}> · {t.status[subStatus] ?? subStatus}</span>
                )}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Plan cards — grid de 4 columnas ─────────────────────────────── */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
          gap={{ xs: 2.5, md: 3 }}
          alignItems="start"
        >
          {PLANS.map((plan, idx) => {
            const limits     = PLAN_LIMITS[plan.key];
            const isCurrent  = currentPlan === plan.key;
            const isLoading  = loadingPlan === plan.key;
            const features   = t.features[plan.key](limits);
            // Precio real que se mostrará como precio principal (y se pasa a Stripe)
            const finalPrice = calcFinalPrice(plan.price, plan.discount);
            // Ahorro mensual (para el tooltip de savings)
            const savedAmt   = plan.discount && plan.price
              ? Math.round((plan.price - finalPrice) * 100) / 100
              : 0;

            return (
              <PlanCard key={plan.key} iscurrent={isCurrent ? 1 : 0} elevation={0} sx={{ animationDelay: `${idx * 0.1}s` }}>

                {/* Badge de descuento — esquina superior derecha */}
                {plan.discount > 0 && (
                  <DiscountBadge
                    pct={plan.discount}
                    isCurrent={isCurrent}
                    label={t.discountBadge(plan.discount)}
                  />
                )}

                {isCurrent && (
                  <Box sx={{ py: 0.85, textAlign: 'center', background: 'rgba(255,255,255,0.15)', borderRadius: '20px 20px 0 0' }}>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.12em', color: '#fff', textTransform: 'uppercase' }}>
                      ✦ {t.currentPlan} ✦
                    </Typography>
                  </Box>
                )}

                <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      background: isCurrent ? 'rgba(255,255,255,0.2)' : '#f5f3ff',
                      border: isCurrent ? '1px solid rgba(255,255,255,0.3)' : '1px solid #ede9fe',
                      color: isCurrent ? '#fff' : '#6366f1',
                    }}
                  >
                    {plan.icon}
                  </Box>

                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '1.05rem', color: isCurrent ? '#fff' : '#111827', mb: 0.3 }}>
                    {plan.label}
                  </Typography>

                  {/* ── Bloque de precio con soporte de descuento ── */}
                  {plan.price === 0 ? (
                    // Plan Free: sin descuento posible
                    <Box mt={1.5} mb={0.5}>
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800, fontSize: '2.1rem', color: isCurrent ? '#fff' : '#111827' }}>
                        {language === 'es' ? 'Gratis' : 'Free'}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Fila de precio original tachado + badge de % cuando hay descuento */}
                      {plan.discount > 0 && (
                        <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                          <Typography
                            sx={{
                              fontFamily: '"DM Sans", sans-serif',
                              fontSize: '0.88rem',
                              fontWeight: 500,
                              textDecoration: 'line-through',
                              color: isCurrent ? 'rgba(255,255,255,0.45)' : '#9ca3af',
                            }}
                          >
                            ${plan.price}
                          </Typography>
                          {/* Savings label inline */}
                          {savedAmt > 0 && (
                            <Typography
                              sx={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                color: isCurrent ? 'rgba(255,255,255,0.6)' : '#f59e0b',
                              }}
                            >
                              {t.savingsLabel(savedAmt)}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Precio final (real / el de Stripe) */}
                      <Box
                        display="flex"
                        alignItems="flex-end"
                        gap={0.5}
                        mt={plan.discount > 0 ? 0.5 : 1.5}
                        mb={0.5}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: 300,
                            fontSize: '1.2rem',
                            color: isCurrent ? 'rgba(255,255,255,0.55)' : '#9ca3af',
                            alignSelf: 'flex-start',
                            mt: 0.8,
                          }}
                        >
                          $
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: 800,
                            fontSize: '2.5rem',
                            lineHeight: 1,
                            color: isCurrent ? '#fff' : '#111827',
                          }}
                        >
                          {finalPrice}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            color: isCurrent ? 'rgba(255,255,255,0.5)' : '#9ca3af',
                            fontSize: '0.77rem',
                            mb: 0.6,
                          }}
                        >
                          {t.currency}
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Divider sx={{ borderColor: isCurrent ? 'rgba(255,255,255,0.15)' : '#f3f4f6', my: 2 }} />

                  <List disablePadding dense sx={{ mb: 3, minHeight: 110 }}>
                    {features.map((f) => (
                      <FeatureItem key={f} text={f} light={isCurrent} />
                    ))}
                  </List>

                  {plan.key === 'free' ? (
                    <ActionButton iscurrent={isCurrent ? 1 : 0} disabled={isCurrent}>
                      {isCurrent ? t.currentPlan : t.startFree}
                    </ActionButton>
                  ) : (
                    <ActionButton
                      iscurrent={isCurrent ? 1 : 0}
                      disabled={isCurrent || !!loadingPlan}
                      onClick={() => handleSubscribe(plan.key)}
                      startIcon={isLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                    >
                      {isCurrent
                        ? t.currentPlan
                        : isLoading
                        ? t.processing
                        : t.subscribeCta(finalPrice)   // ← precio real (con descuento aplicado)
                      }
                    </ActionButton>
                  )}
                </CardContent>
              </PlanCard>
            );
          })}
        </Box>

        {/* Sección inferior: detalles + portal */}
        {showBottomSection && (
          <Box
            mt={5}
            sx={{
              width: '96%',
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: gridCols,
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {showDetails && (
              <SubscriptionInfoInline subscription={subscription} t={t} lang={language} />
            )}
            {showPortal && (
              <PortalCard
                t={t}
                stripeOverlay={stripeOverlay}
                onOpenPortal={handlePortal}
              />
            )}
          </Box>
        )}

        <Typography textAlign="center" mt={5} sx={{ fontFamily: '"DM Sans", sans-serif', color: '#d1d5db', fontSize: '0.73rem' }}>
          {t.footer}
        </Typography>
      </Container>

      {/* Stripe overlay */}
      {stripeOverlay && <StripeOverlay type={stripeOverlay} t={t} />}

      {/* Confirm dialog */}
      <Dialog
        open={!!confirmPlan}
        onClose={() => setConfirmPlan(null)}
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, color: '#111827' }}>
          {t.confirmTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#6b7280', fontFamily: '"DM Sans", sans-serif' }}>
            {confirmPlan && t.confirmBody(confirmPlan)}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmPlan(null)}
            sx={{ color: '#6b7280', fontFamily: '"DM Sans", sans-serif', textTransform: 'none', borderRadius: 2 }}
          >
            {t.cancelBtn}
          </Button>
          <Button
            onClick={confirmSubscribe}
            sx={{
              background: 'linear-gradient(135deg,#6366f1,#7c3aed)',
              color: '#fff',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 10,
              px: 3,
              boxShadow: '0 4px 14px rgba(99,102,241,0.3)',
              '&:hover': { background: 'linear-gradient(135deg,#4f46e5,#6d28d9)' },
            }}
          >
            {t.goToPayment}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSnackOpen(false)} sx={{ fontFamily: '"DM Sans", sans-serif' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
}